import React from 'react'
import { graphql } from 'relay-runtime'
import styled from '@emotion/styled'
import { useFragment, useMutation } from 'react-relay'
import { toast } from 'react-toastify'
import {
  setCampaignModalOpen,
  setCreativeDrawerData,
  setCreativeDrawerOpen,
} from '../../../state/appSlice'
import { useAppDispatch } from '../../../state/hooks'
import {
  Box,
  Button,
  FormControlLabel,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import ForumIcon from '@mui/icons-material/Forum'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import { MetaAdInteractiveOverlay } from './MetaAdInteractiveOverlay'
import { theme } from '../../../mui'
import { e0Grey } from '../../../colors'
import { metaAdApproveMutation } from './mutations/MetaAdApprove'
import { useUserRoleCheck } from '../../../hooks'
import { useTrackingContext } from '../../../analytics/useTracking'
import { trackAdApproved, trackAdDeleted, trackAdDuplicated } from '../../../analytics/tracking'
import { useTypeParam } from '../../../state/url/type'
import { CreativeDrawerView } from '../CreativeDrawer'
import { getMetaAdListQueryVariables } from '../../../pages/HomeRoute'
import MetaAdPreview from '../MetaAdPreview'
import { MetaAd$key } from './__generated__/MetaAd.graphql'
import { MetaAdApproveMutation } from './mutations/__generated__/MetaAdApproveMutation.graphql'
import { MetaAdCreateMetaAdMutation } from './__generated__/MetaAdCreateMetaAdMutation.graphql'
import { MetaAdCopyVisualAssetMutation } from './__generated__/MetaAdCopyVisualAssetMutation.graphql'
import { MetaAdDeleteMetaAdMutation } from './__generated__/MetaAdDeleteMetaAdMutation.graphql'

interface MetaAdProps {
  fragmentRequest: MetaAd$key
}

export const metaAdFragment = graphql`
  fragment MetaAd on MetaAd {
    ...MetaAdPreview
    ...CreativeDrawerMetaAd
    id
    approved
    submittedForApproval
    created
    name
    callToAction
    description
    destinationUrl
    displayUrl
    headline
    primaryText
    facebookPageId
    instagramAccountId
    brand {
      id
    }
    comments {
      id
    }
    visualAssets {
      id
    }
  }
`

const createMetaAdMutation = graphql`
  mutation MetaAdCreateMetaAdMutation(
    $input: CreateMetaAdRequest!
    $metaAdsFilter: MetaAdsFilter!
  ) {
    createMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      brandId
      callToAction
      name
      description
      destinationUrl
      displayUrl
      headline
      primaryText
      facebookPageId
      instagramAccountId
      brand {
        id
        draftMetaAdCount
        ...CreativeList
      }
    }
  }
`

const copyVisualAssetMutation = graphql`
  mutation MetaAdCopyVisualAssetMutation(
    $input: CopyVisualAssetRequest!
  ) {
    copyVisualAsset(input: $input) {
      id
      metaAdId
    }
  }
`

const deleteMetaAdMutation = graphql`
  mutation MetaAdDeleteMetaAdMutation(
    $input: DeleteRequest!
    $metaAdsFilter: MetaAdsFilter!
  ) {
    deleteMetaAd(input: $input, metaAdsFilter: $metaAdsFilter) {
      id
      brand {
        id
        draftMetaAdCount
        ...CreativeList
      }
    }
  }
`

interface OverlayProps {
  active: boolean
  approved: boolean
}

export const S = {
  Wrapper: styled.div`
    width: fit-content;
    position: relative;
    padding-left: 12px;
  `,
  Header: styled.div`
    margin: 0 0 1rem 0;
    display: flex;
    justify-content: space-between;
    & > * {
      margin-left: 10px;
    }
  `,
  Footer: styled.div`
    margin: 1rem 0 0 0;
    display: flex;
    justify-content: space-between;
    & > * {
      margin-left: 10px;
    }
  `,
  Comments: styled.div`
    color: ${theme.palette.primary.main};
    cursor: pointer;
    vertical-align: bottom;
    margin-top: auto;
  `,
  ApprovalSlider: styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
  `,
  Overlay: styled.div<OverlayProps>`
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    transition: 0.5s;
    background-color: ${(props) =>
      props.approved ? 'rgba(255, 255, 255, .6)' : 'rgba(255, 255, 255, 0)'};
    color: ${(props) =>
      props.approved ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)'};
    cursor: ${(props) => (props.approved ? 'unset' : 'pointer')};
    &:hover {
      background-color: rgba(255, 255, 255, 0.6);
      color: rgba(255, 255, 255, 1);
    }
    & > * {
      transform: scale(5);
    }
  `,
  EditIcon: styled(EditIcon)`
    font-size: inherit;
    margin-right: 5px;
  `,
  ForumIcon: styled(ForumIcon)`
    font-size: inherit;
    margin-right: 5px;
  `,
  DeleteIcon: styled(DeleteIcon)`
    font-size: inherit;
    margin-right: 5px;
  `,
  FileCopyIcon: styled(FileCopyIcon)`
    font-size: inherit;
    margin-right: 5px;
  `,
  MetaAdPreviewWrapper: styled.div`
    position: relative;
    width: 20rem;
    height: 34rem;
    padding: 1rem;
    background-color: ${e0Grey};
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.375em;
    font-size: 0.875rem;
    color: ${theme.palette.text.disabled};
  `,
}

const MetaAd = ({ fragmentRequest }: MetaAdProps) => {
  const metaAd = useFragment(metaAdFragment, fragmentRequest)
  const dispatch = useAppDispatch()
  const [approveMetaAdRequest, approveMetaAdLoading] =
    useMutation<MetaAdApproveMutation>(metaAdApproveMutation)
  const [createMetaAdRequest] =
    useMutation<MetaAdCreateMetaAdMutation>(createMetaAdMutation)
  const [copyVisualAssetRequest] =
    useMutation<MetaAdCopyVisualAssetMutation>(copyVisualAssetMutation)
  const [deleteMetaAdRequest] =
    useMutation<MetaAdDeleteMetaAdMutation>(deleteMetaAdMutation)
  const canEditAd = useUserRoleCheck({ roles: ['EDITOR'] })
  const canApproveAd =
    useUserRoleCheck({ roles: ['APPROVER', 'EDITOR'] }) &&
    metaAd?.submittedForApproval
  const [type] = useTypeParam()
  const ctx = useTrackingContext()

  // Menu dropdown
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const openDropdown = Boolean(anchorEl)
  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const onClick = (view: CreativeDrawerView) => {
    dispatch(setCreativeDrawerData({ metaAdFragmentRequest: metaAd, view }))
    dispatch(setCreativeDrawerOpen(true))
    handleDropdownClose()
  }

  const onDuplicateClick = () => {
    createMetaAdRequest({
      variables: {
        input: {
          brandId: metaAd.brand.id,
          callToAction: metaAd.callToAction,
          name: `${metaAd.name} (Copy)`,
          description: metaAd.description,
          destinationUrl: metaAd.destinationUrl,
          displayUrl: metaAd.displayUrl,
          headline: metaAd.headline,
          primaryText: metaAd.primaryText,
          facebookPageId: metaAd.facebookPageId,
          instagramAccountId: metaAd.instagramAccountId,
        },
        ...getMetaAdListQueryVariables(type),
      },
      onCompleted: (data) => {
        copyVisualAssetRequest({
          variables: {
            input: {
              id: metaAd.visualAssets[0].id,
              metaAdId: data.createMetaAd.id,
            },
          },
          onCompleted: () => {
            toast.success(`Successfully duplicated ad.`)
            trackAdDuplicated({ ad_id: metaAd.id, brand_id: metaAd.brand.id, account_id: ctx.accountId }, ctx)
            dispatch(setCampaignModalOpen(false))
          }
        },
        )
      },
      onError: (error: Error) => {
        console.log(error)
        toast.error(`Unable to duplicate ad.`)
      },
    })
  }

  const onDeleteClick = () => {
    deleteMetaAdRequest({
      variables: {
        input: {
          id: metaAd.id,
        },
        ...getMetaAdListQueryVariables(type),
      },
      onCompleted: () => {
        toast.success(`Successfully deleted ad.`)
        trackAdDeleted({ ad_id: metaAd.id, brand_id: metaAd.brand.id, account_id: ctx.accountId }, ctx)
        dispatch(setCampaignModalOpen(false))
      },
      onError: (error: Error) => {
        console.log(error)
        toast.error(`Unable to delete ad.`)
      },
    })
  }

  const handleApprovalChange = () => {
    const onCompleted = () => {
      toast.success(
        `Successfully ${metaAd?.approved ? 'unapproved' : 'approved'} ad.`
      )
      trackAdApproved({ ad_id: metaAd.id, brand_id: metaAd.brand.id, account_id: ctx.accountId, is_approved: !metaAd.approved }, ctx)
      dispatch(setCampaignModalOpen(false))
    }
    const onError = (error: Error) => {
      console.log(error)
      toast.error(`Unable to approve ad.`)
    }
    approveMetaAdRequest({
      variables: {
        input: {
          id: metaAd!.id,
          approved: !metaAd?.approved,
        },
        ...getMetaAdListQueryVariables(type),
      },
      onCompleted,
      onError,
    })
  }

  const approveSlider = metaAd?.submittedForApproval && !metaAd?.created && (
    <>
      <S.ApprovalSlider>
        <FormControlLabel
          control={
            <Switch
              checked={metaAd?.approved}
              onChange={handleApprovalChange}
              disabled={!canApproveAd || approveMetaAdLoading}
            />
          }
          label="Approved "
        />
      </S.ApprovalSlider>
    </>
  )

  const approveAd = () => {
    if (!metaAd.approved && !approveMetaAdLoading) {
      const onCompleted = () => {
        toast.success(`Successfully approved ad.`)
        trackAdApproved({ ad_id: metaAd.id, brand_id: metaAd.brand.id, account_id: ctx.accountId, is_approved: true }, ctx)
        dispatch(setCampaignModalOpen(false))
      }
      const onError = (error: Error) => {
        console.log(error)
        toast.error(`Unable to approve ad.`)
      }
      approveMetaAdRequest({
        variables: {
          input: {
            id: metaAd.id,
            approved: true,
          },
          ...getMetaAdListQueryVariables(type),
        },
        onCompleted,
        onError,
      })
    }
  }

  const handleOverlayClick = () => {
    if (metaAd.approved && !metaAd.created) {
      // toggle selection
    } else if (!metaAd.submittedForApproval) {
      // toggle selection
    } else if (metaAd.submittedForApproval) {
      // approveAd
      approveAd()
    }
    // do nothing!
  }

  const approveOverlay = (
    <>
      <S.Overlay
        active={metaAd.submittedForApproval}
        approved={metaAd.approved || approveMetaAdLoading}
        onClick={handleOverlayClick}
      >
        <CheckCircleIcon />
      </S.Overlay>
    </>
  )

  const primaryCTA =
    canEditAd && !metaAd?.approved ? (
      <Button
        disabled={metaAd.approved}
        onClick={() => onClick(CreativeDrawerView.TEXT)}
        disableRipple
      >
        <S.EditIcon />
        Edit
      </Button>
    ) : (
      <Button
        onClick={() => onClick(CreativeDrawerView.COMMENTS)}
        disableRipple
      >
        <S.ForumIcon />
        Comment
      </Button>
    )

  return (
    <>
      <S.Wrapper>
        <S.Header>
          <Box sx={{ width: 250 }}>
            <Tooltip title={metaAd.name} enterDelay={500} enterNextDelay={500}>
              <Typography noWrap>{metaAd.name}</Typography>
            </Tooltip>
          </Box>
          <div>
            <Tooltip title={'More Options'}>
              <Button
                id="dropdown-button"
                aria-controls={openDropdown ? 'dropdown-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openDropdown ? 'true' : undefined}
                variant="text"
                disableElevation
                onClick={handleDropdownClick}
              >
                <MoreHorizIcon />
              </Button>
            </Tooltip>
            <Menu
              id="dropdown-menu"
              MenuListProps={{
                'aria-labelledby': 'dropdown-button',
              }}
              anchorEl={anchorEl}
              open={openDropdown}
              onClose={handleDropdownClose}
            >
              {canEditAd && (
                <MenuItem
                  disabled={metaAd.approved}
                  onClick={() => onClick(CreativeDrawerView.TEXT)}
                  disableRipple
                >
                  <S.EditIcon />
                  Edit
                </MenuItem>
              )}
              <MenuItem
                onClick={() => onClick(CreativeDrawerView.COMMENTS)}
                disableRipple
              >
                <S.ForumIcon />
                Comment{metaAd.comments.length > 1 ? 's' : ''}{' '}
                {metaAd.comments.length > 0
                  ? '(' + metaAd.comments.length + ')'
                  : ''}
              </MenuItem>
              <MenuItem onClick={onDuplicateClick} disableRipple>
                <S.FileCopyIcon />
                Duplicate
              </MenuItem>
              <MenuItem onClick={onDeleteClick} disableRipple>
                <S.DeleteIcon />
                Delete
              </MenuItem>
            </Menu>
          </div>
        </S.Header>
        <S.MetaAdPreviewWrapper>
          <MetaAdInteractiveOverlay metaAd={metaAd} />
          <MetaAdPreview fragmentRequest={metaAd} />
        </S.MetaAdPreviewWrapper>
        <S.Footer>
          <div>{approveSlider}</div>
          <div>{primaryCTA}</div>
        </S.Footer>
      </S.Wrapper>
    </>
  )
}

export default MetaAd
